// assets/section-frequently-bought-together.js
// Frequently-bought-together controller: picks 2 random cross-sells, mirrors the
// current product's selected variant, keeps a live total, and adds the selected
// items to cart in one request.
(() => {
  const app = window.Digitaliz;
  if (!app) return;
  window.DigitalizModules = window.DigitalizModules || {};
  if (window.DigitalizModules.FrequentlyBoughtTogether) return;

  const MIN_QTY = (window.themeSettings && Number(window.themeSettings.minQty)) || 2;

  class FrequentlyBoughtTogether {
    constructor(el) {
      this.el = el;
      this.openCartDrawer = el.dataset.fbtOpenDrawer === "true";
      this.grid = el.querySelector("[data-fbt-mount]");
      this.pool = el.querySelector("[data-fbt-pool]");
      this.totalEl = el.querySelector("[data-fbt-total]");
      this.addBtn = el.querySelector("[data-fbt-add]");
      this.items = [];
      this.isAdding = false;
      this.init();
    }

    init() {
      if (!this.grid) return;
      this._mountRandomPair();
      this._registerItems();
      this._bindItemInteractions();
      this._bindEvents();
      // Pull any variant a card already resolved synchronously on mount (its
      // change event would have fired before _bindEvents ran).
      this._pullResolvedVariants();
      this._syncCheckboxAvailability();
      this._updateTotal();
    }

    // Clone 2 random candidate cards from the inert <template> into the grid.
    _mountRandomPair() {
      if (!this.pool) return;
      const candidates = [...this.pool.content.children];
      if (!candidates.length) return;
      this._sample(candidates, 2).forEach((node) => {
        this.grid.appendChild(document.importNode(node, true));
      });
      // Mount the shared product-card behaviour on the freshly added cards.
      const PC = window.DigitalizModules.ProductCard;
      if (PC && typeof PC.initWithin === "function") PC.initWithin(this.grid);
    }

    // Fisher–Yates: pick n distinct random elements.
    _sample(arr, n) {
      const pool = arr.slice();
      for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
      }
      return pool.slice(0, Math.min(n, pool.length));
    }

    // Build the item model from the DOM (current product + mounted FBT cards).
    _registerItems() {
      this.items = [...this.grid.querySelectorAll("[data-fbt-item]")].map((itemEl) => {
        const type = itemEl.dataset.fbtType;
        const item = {
          type,
          el: itemEl,
          checkbox: itemEl.querySelector("[data-fbt-checkbox]"),
          variantId: null,
          price: 0,
          available: false,
          resolved: false,
          quantity: MIN_QTY
        };

        if (type === "current") {
          const card = itemEl.querySelector("[data-fbt-current]");
          if (card) {
            item.variantId = Number(card.dataset.variantId) || null;
            item.price = Number(card.dataset.variantPrice) || 0;
            item.available = card.dataset.available === "true";
            item.resolved = true;
            item.priceEl = card.querySelector("[data-fbt-current-price]");
            item.variantEl = card.querySelector("[data-fbt-current-variant]");
            item.imageEl = card.querySelector("[data-fbt-current-image]");
            item.deliveryEl = itemEl.querySelector("[data-fbt-current-delivery]");
            item.deliveryTextEl = itemEl.querySelector("[data-fbt-current-delivery-text]");
            item.leadTimes = this._parseLeadTimes(itemEl.querySelector("[data-fbt-current-lead-times]"));
          }
        }
        // FBT items resolve their variant via digitaliz:product-card:change.
        return item;
      });
    }

    _bindEvents() {
      this.el.addEventListener("change", (event) => {
        if (event.target.matches("[data-fbt-checkbox]")) this._updateTotal();
      });

      // FBT card variant resolution (default selection on load + user changes).
      this.el.addEventListener("digitaliz:product-card:change", (event) => {
        const itemEl = event.target.closest("[data-fbt-item]");
        if (!itemEl) return;
        const item = this.items.find((i) => i.el === itemEl);
        if (!item) return;
        this._applyFbtVariant(item, event.detail && event.detail.variant);
      });

      // Current product mirrors the main product form's selected variant.
      app.on(app.events.VARIANT_CHANGE, (event) => {
        const current = this.items.find((i) => i.type === "current");
        if (!current) return;
        const variant = event.detail && event.detail.variant;
        if (!variant) return;
        current.variantId = variant.id;
        if (typeof variant.price === "number") current.price = variant.price;
        current.available = Boolean(variant.available);
        if (current.priceEl) app.setCurrency(current.priceEl, current.price);
        if (current.variantEl) current.variantEl.textContent = variant.title || "";
        if (current.imageEl && variant.featured_image && variant.featured_image.src) {
          current.imageEl.srcset = "";
          current.imageEl.src = variant.featured_image.src;
        }
        this._updateCurrentLead(current, variant.id);
        this._syncCheckboxAvailability();
        this._updateTotal();
      });

      if (this.addBtn) {
        this.addBtn.addEventListener("click", () => this._addSelected());
      }
    }

    // Click anywhere on a card toggles its checkbox, except: option buttons and
    // the checkbox handle themselves; the title and price bar navigate to the
    // product (when the card links to one).
    _bindItemInteractions() {
      this.items.forEach((item) => {
        const link = item.el.querySelector(".product-card__card-link");
        item.el.addEventListener("click", (event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;
          if (target.closest("[data-pc-option-btn], [data-fbt-checkbox], .fbt__select")) return;
          // Read at click time: the product card swaps the href to the chosen variant.
          const href = link ? link.getAttribute("href") : null;
          if (href && target.closest(".product-card__title, .product-card__price-bar")) {
            window.location.href = href;
            return;
          }
          if (!item.checkbox || item.checkbox.disabled) return;
          item.checkbox.checked = !item.checkbox.checked;
          this._updateTotal();
        });
      });
    }

    _parseLeadTimes(node) {
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return null;
      }
    }

    // Mirror the current product's lead time to the selected variant (variant
    // override → product-level fallback), matching the product-card behaviour.
    _updateCurrentLead(current, variantId) {
      if (!current.deliveryEl || !current.deliveryTextEl) return;
      const lt = current.leadTimes;
      let text = lt && lt.fallback ? lt.fallback : "";
      if (lt && lt.variants && lt.variants[variantId]) text = lt.variants[variantId];
      current.deliveryTextEl.textContent = text;
      current.deliveryEl.classList.toggle("is-hidden", text.trim() === "");
    }

    _applyFbtVariant(item, variant) {
      item.variantId = variant ? variant.id : null;
      item.price = variant ? variant.price : 0;
      item.available = Boolean(variant && variant.available);
      item.resolved = true;
      this._syncCheckboxAvailability();
      this._updateTotal();
    }

    // Read variants that a card resolved before our listener was attached.
    _pullResolvedVariants() {
      this.items.forEach((item) => {
        if (item.type !== "fbt") return;
        const cardEl = item.el.querySelector("[data-product-card]");
        const pc = cardEl && cardEl.__digitalizCard;
        if (pc && pc.activeVariant) this._applyFbtVariant(item, pc.activeVariant);
      });
    }

    _syncCheckboxAvailability() {
      this.items.forEach((item) => {
        // Leave unresolved FBT items untouched so their default checked state
        // (the "pre-select" setting) survives until their variant resolves.
        if (!item.checkbox || !item.resolved) return;
        const usable = Boolean(item.available && item.variantId);
        item.checkbox.disabled = !usable;
        item.el.classList.toggle("is-unavailable", !usable);
        if (!usable) item.checkbox.checked = false;
      });
    }

    _selectedItems() {
      return this.items.filter(
        (i) => i.checkbox && i.checkbox.checked && i.available && i.variantId
      );
    }

    _updateTotal() {
      const total = this._selectedItems().reduce((sum, i) => sum + i.price * i.quantity, 0);
      if (this.totalEl) app.setCurrency(this.totalEl, total);
      this._refreshButtonState();
    }

    _refreshButtonState() {
      if (!this.addBtn) return;
      const disabled = this.isAdding || this._selectedItems().length === 0;
      this.addBtn.disabled = disabled;
      this.addBtn.classList.toggle("is-disabled", disabled);
    }

    async _addSelected() {
      if (this.isAdding) return;
      const selected = this._selectedItems();
      if (!selected.length) return;

      // Every item follows the theme minimum-quantity rule (no exemption).
      const items = selected.map((i) => ({ id: i.variantId, quantity: i.quantity }));

      this.isAdding = true;
      this._setLoading(true);
      try {
        if (!app.cart || typeof app.cart.add !== "function") {
          throw new Error("Cart unavailable");
        }
        await app.cart.add(items);
        if (this.openCartDrawer) {
          app.openDrawer("CartDrawer");
        } else {
          window.location.href = "/cart";
        }
      } catch (error) {
        // Errors surface via the CART_ERROR toast.
      } finally {
        this.isAdding = false;
        this._setLoading(false);
        this._refreshButtonState();
      }
    }

    _setLoading(loading) {
      if (!this.addBtn) return;
      if (loading) {
        // Pin the current width so swapping in the shorter "Adding…" label does
        // not shrink the button (it would otherwise hug the smaller text).
        this.addBtn.style.minWidth = this.addBtn.offsetWidth + "px";
        this.addBtn.disabled = true;
        this.addBtn.classList.add("is-disabled");
        if (this._originalLabel == null) {
          this._originalLabel = this.addBtn.textContent;
          this.addBtn.textContent =
            (window.themeStrings && window.themeStrings.addingToCart) || this.addBtn.textContent;
        }
      } else {
        this.addBtn.style.minWidth = "";
        if (this._originalLabel != null) {
          this.addBtn.textContent = this._originalLabel;
          this._originalLabel = null;
        }
      }
    }
  }

  window.DigitalizModules.FrequentlyBoughtTogether = FrequentlyBoughtTogether;

  function initSections(root) {
    (root || document).querySelectorAll("[data-fbt]").forEach((el) => {
      if (el.dataset.fbtBound === "true") return;
      el.dataset.fbtBound = "true";
      new FrequentlyBoughtTogether(el);
    });
  }

  if (document.readyState !== "loading") {
    initSections(document);
  } else {
    document.addEventListener("DOMContentLoaded", () => initSections(document));
  }

  document.addEventListener("shopify:section:load", (event) => initSections(event.target));
})();
